import { Box, Flex, Grid, Heading, HStack, Icon, Image, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { FaGraduationCap, FaLaptopCode, FaSchool, FaShieldAlt } from "react-icons/fa";
import { FiAward } from "react-icons/fi";
import { portfolio, type EducationItem } from "../data/portfolio";
import { MotionFade } from "./MotionFade";
import { Accent, SectionHeading } from "./SectionHeading";
import { SpotlightCard } from "./SpotlightCard";

const educationIcons: Record<EducationItem["icon"], IconType> = {
  degree: FaGraduationCap,
  school: FaSchool,
  security: FaShieldAlt,
  course: FaLaptopCode,
};

function Label({ children }: { children: string }) {
  return (
    <Text fontFamily="mono" fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="accent.300" mb={6}>
      {children}
    </Text>
  );
}

export function AboutSection() {
  const { person, about, education, honors } = portfolio;

  return (
    <Box id="sobre" as="section" py={{ base: 20, md: 32 }} px={{ base: 5, md: 8 }} scrollMarginTop="72px">
      <Box maxW="container.xl" mx="auto">
        <SectionHeading
          section="sobre"
          title={
            <>
              Quem está <Accent>por trás</Accent> do código
            </>
          }
        />

        <Grid templateColumns={{ base: "1fr", lg: "minmax(0, 5fr) minmax(0, 7fr)" }} gap={{ base: 14, lg: 20 }}>
          <Box>
            <MotionFade>
              <Box position="relative" w="fit-content" mb={8}>
                <Box
                  position="absolute"
                  inset={0}
                  borderRadius="3xl"
                  borderWidth="1px"
                  borderColor="accent.400"
                  transform="translate(12px, 12px)"
                  aria-hidden
                />
                <Image
                  src={person.profilePhoto}
                  alt={`Retrato profissional de ${person.fullName}`}
                  position="relative"
                  boxSize={{ base: "180px", md: "220px" }}
                  borderRadius="3xl"
                  objectFit="cover"
                  bg="graphite.800"
                />
              </Box>
            </MotionFade>
            <VStack align="stretch" spacing={5}>
              {about.paragraphs.map((paragraph, i) => (
                <MotionFade key={i} delay={i * 0.06}>
                  <Text fontSize={{ base: "md", md: "lg" }} lineHeight="tall" color="graphite.300">
                    {paragraph}
                  </Text>
                </MotionFade>
              ))}
            </VStack>
          </Box>

          <Box>
            <MotionFade>
              <Label>Como eu trabalho</Label>
            </MotionFade>
            <VStack align="stretch" spacing={4}>
              {about.approach.map((item, i) => (
                <MotionFade key={item.title} delay={i * 0.08}>
                  <SpotlightCard
                    p={{ base: 6, md: 8 }}
                    borderRadius="2xl"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                    bg="whiteAlpha.50"
                  >
                    <Flex gap={{ base: 4, md: 7 }} align="flex-start">
                      <Text
                        fontFamily="accent"
                        fontStyle="italic"
                        fontSize={{ base: "4xl", md: "5xl" }}
                        lineHeight="0.9"
                        color="accent.300"
                        flexShrink={0}
                      >
                        {i + 1}
                      </Text>
                      <Box>
                        <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} letterSpacing="-0.02em" color="white">
                          {item.title}
                        </Heading>
                        <Text mt={2} color="graphite.300" lineHeight="tall">
                          {item.text}
                        </Text>
                      </Box>
                    </Flex>
                  </SpotlightCard>
                </MotionFade>
              ))}
            </VStack>
          </Box>
        </Grid>

        <Box mt={{ base: 16, md: 24 }}>
          <MotionFade>
            <Label>Formação e reconhecimento</Label>
          </MotionFade>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={12} spacingY={0}>
            {honors.map((honor) => (
              <MotionFade key={honor}>
                <HStack align="flex-start" spacing={4} py={5} borderTopWidth="1px" borderColor="accent.400">
                  <Flex flexShrink={0} w="44px" h="44px" borderRadius="full" bg="accent.400" color="graphite.950" align="center" justify="center">
                    <Icon as={FiAward} boxSize={5} />
                  </Flex>
                  <Box>
                    <Text fontWeight="700" color="white">
                      {honor}
                    </Text>
                    <Text fontSize="sm" color="graphite.400" mt={1}>
                      Reconhecimento
                    </Text>
                  </Box>
                </HStack>
              </MotionFade>
            ))}
            {education.map((edu, i) => (
              <MotionFade key={`${edu.title}-${edu.period}`} delay={0.05 + i * 0.05}>
                <HStack align="flex-start" spacing={4} py={5} borderTopWidth="1px" borderColor="whiteAlpha.200">
                  <Flex flexShrink={0} w="44px" h="44px" borderRadius="full" bg="whiteAlpha.100" color="accent.300" align="center" justify="center">
                    <Icon as={educationIcons[edu.icon]} boxSize={5} />
                  </Flex>
                  <Box>
                    <Text fontWeight="700" color="white">
                      {edu.title}
                    </Text>
                    <Text fontSize="sm" color="graphite.300" mt={1}>
                      {edu.institution}
                    </Text>
                    <Text fontFamily="mono" fontSize="xs" color="graphite.400" mt={1}>
                      {edu.period.replace(", ", " → ")}
                    </Text>
                  </Box>
                </HStack>
              </MotionFade>
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    </Box>
  );
}
